import React, { useState } from 'react';
import { useNavigate } from 'react-router';
// @mui
import { alpha } from '@mui/material/styles';
import { Box, Divider, MenuItem, Stack, Typography } from '@mui/material';
// routes
import { PATH_AUTH } from '../../../routes/paths';
// auth
import { useAuthContext } from '../../../auth/useAuthContext';
// components
import { useSnackbar } from 'notistack';
import MenuPopover from '../../../components/menu-popover';
import { IconButtonAnimate } from '../../../components/animate';
import { useLocales } from '../../../locales';
import Iconify from 'src/components/iconify';
import { useSettingsContext } from '../../../components/settings';
import Image from '../../../components/image';
// ----------------------------------------------------------------------

export default function AccountPopover() {
  const navigate = useNavigate();
  const { translate, allLangs, currentLang, onChangeLang } = useLocales();

  const { user, logout } = useAuthContext();
  const { themeMode, themeLayout, onToggleMode, onToggleLayout } = useSettingsContext();

  const { enqueueSnackbar } = useSnackbar();

  const [openPopover, setOpenPopover] = useState<HTMLElement | null>(null);

  const handleOpenPopover = (event: React.MouseEvent<HTMLElement>) => {
    setOpenPopover(event.currentTarget);
  };

  const handleClosePopover = () => {
    setOpenPopover(null);
  };

  const handleLogout = async () => {
    try {
      logout();
      navigate(PATH_AUTH.login, { replace: true });
      handleClosePopover();
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to logout!', { variant: 'error' });
    }
  };

  const handleClearSettings = () => {
    localStorage.clear();
    window.location.reload();
  };

  const handleToggleMode = () => {
    onToggleMode();
  };

  const handleToggleLayout = () => {
    onToggleLayout();
  };

  const handleChangeLang = (newLang: string) => {
    onChangeLang(newLang);
    handleClosePopover();
  };

  return (
    <>
      <IconButtonAnimate
        onClick={handleOpenPopover}
        sx={{
          p: 0,
          width: 40,
          height: 40,
          borderRadius: 1.25,
          '&:hover': { bgcolor: 'action.hover' },
          ...(openPopover && {
            '&:before': {
              zIndex: 1,
              content: "''",
              width: '100%',
              height: '100%',
              borderRadius: 1.25,
              position: 'absolute',
              bgcolor: (theme) => alpha(theme.palette.grey[900], 0.8),
            },
          }),
        }}
      >
        {/* <CustomAvatar src={user?.photoURL}   alt={user?.displayName} name={user?.displayName} /> */}
        <Iconify icon="mdi:user" width={22} />
      </IconButtonAnimate>

      <MenuPopover open={openPopover} onClose={handleClosePopover} sx={{ width: 200, p: 0 }}>
        <Box sx={{ my: 1.5, px: 2.5 }}>
          <Typography variant="subtitle2" noWrap>
            {user?.displayName}
          </Typography>

          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {user?.email}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <MenuItem onClick={handleToggleMode} sx={{ m: 1 }}>
          <Iconify
            icon={themeMode === 'light' ? 'ic:round-dark-mode' : 'ic:round-light-mode'}
            sx={{ mr: 1 }}
          />
          {themeMode === 'light' ? `${translate('darkMode')}` : `${translate('lightMode')}`}
        </MenuItem>

        <MenuItem onClick={handleToggleLayout} sx={{ m: 1 }}>
          <Iconify
            icon={themeLayout === 'vertical' ? 'ic:round-view-sidebar' : 'ic:round-view-compact'}
            sx={{ mr: 1 }}
          />
          {themeLayout === 'vertical' ? 'Mini Layout' : 'Vertical Layout'}
        </MenuItem>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Stack sx={{ py: 0.5 }}>
          {allLangs.map((option) => (
            <MenuItem
              key={option.value}
              selected={option.value === currentLang.value}
              onClick={() => handleChangeLang(option.value)}
              sx={{ m: 1 }}
            >
              <Image
                disabledEffect
                alt={option.label}
                src={option.icon}
                sx={{ width: 22, height: 22, borderRadius: '50%', mr: 1.5 }}
              />
              {option.label}
            </MenuItem>
          ))}
        </Stack>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <MenuItem onClick={handleClearSettings} sx={{ m: 1 }}>
          {`${translate('resetSettings')}`}
        </MenuItem>

        <MenuItem onClick={handleLogout} sx={{ m: 1 }}>
          {`${translate('logout')}`}
        </MenuItem>
      </MenuPopover>
    </>
  );
}
